import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import { userPool } from "./pool";

/**
 * Promise wrappers over {@code amazon-cognito-identity-js}, which is
 * callback-shaped. Nothing in here knows about React.
 *
 * <h2>Why this library and not the v3 AWS SDK</h2>
 * It implements <b>SRP</b> ({@code USER_SRP_AUTH}): the password is never sent
 * to AWS, only a zero-knowledge proof derived from it. The v3 SDK has no SRP
 * helper, so a custom UI on it has to fall back to {@code USER_PASSWORD_AUTH},
 * which posts the plaintext password (TLS-protected, but recoverable at the
 * endpoint). For a sign-in page we own, SRP is the right default — and it needs
 * no change to the app client, which already allows {@code userSrp} only.
 *
 * <h2>Errors</h2>
 * Every rejection is a {@link CognitoAuthError} carrying Cognito's error
 * {@code code}. Callers translate the code (see {@link authErrorKey}) rather
 * than rendering {@code err.message} — AWS's messages are English-only, and
 * showing them would put untranslated text on a page whose entire point is
 * being in the user's language.
 */

/** Cognito's error shape, normalised. {@code code} is the discriminator. */
export class CognitoAuthError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "CognitoAuthError";
    this.code = code;
  }
}

function toAuthError(err: unknown): CognitoAuthError {
  if (err instanceof CognitoAuthError) return err;
  const e = err as { code?: string; name?: string; message?: string } | null;
  return new CognitoAuthError(
    e?.code ?? e?.name ?? "UnknownError",
    e?.message ?? "Authentication failed",
  );
}

/**
 * i18n key for a Cognito error code, under the {@code auth.errors.*} namespace.
 * Unmapped codes fall back to a generic message — better an honest generic
 * sentence in the right language than a precise one in the wrong one.
 */
export function authErrorKey(code: string): string {
  switch (code) {
    case "NotAuthorizedException":
      return "auth.errors.notAuthorized";
    case "UserNotFoundException":
      return "auth.errors.notAuthorized"; // same message: don't confirm existence
    case "UserNotConfirmedException":
      return "auth.errors.notConfirmed";
    case "UsernameExistsException":
      return "auth.errors.usernameExists";
    case "CodeMismatchException":
      return "auth.errors.codeMismatch";
    case "ExpiredCodeException":
      return "auth.errors.codeExpired";
    case "InvalidPasswordException":
      return "auth.errors.invalidPassword";
    case "InvalidParameterException":
      return "auth.errors.invalidParameter";
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return "auth.errors.tooManyAttempts";
    case "PasswordResetRequiredException":
      return "auth.errors.passwordResetRequired";
    case "NetworkError":
      return "auth.errors.network";
    default:
      return "auth.errors.generic";
  }
}

function requirePool() {
  const pool = userPool();
  if (!pool) {
    throw new CognitoAuthError(
      "NotConfigured",
      "VITE_OIDC_AUTHORITY is not a Cognito issuer — native sign-in is unavailable",
    );
  }
  return pool;
}

function cognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email.trim(), Pool: requirePool() });
}

/**
 * Outcome of a sign-in attempt. {@code NEW_PASSWORD_REQUIRED} is reachable for
 * admin-created users, who are handed a temporary password and must replace it
 * before they hold a session.
 */
export type SignInResult =
  | { status: "SUCCESS"; session: CognitoUserSession }
  | { status: "NEW_PASSWORD_REQUIRED"; user: CognitoUser; requiredAttributes: string[] };

/** SRP sign-in. Rejects with {@link CognitoAuthError}. */
export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = cognitoUser(email);
    user.authenticateUser(
      new AuthenticationDetails({ Username: email.trim(), Password: password }),
      {
        onSuccess: (session) => resolve({ status: "SUCCESS", session }),
        onFailure: (err) => reject(toAuthError(err)),
        newPasswordRequired: (_attrs, requiredAttributes) =>
          resolve({
            status: "NEW_PASSWORD_REQUIRED",
            user,
            requiredAttributes: (requiredAttributes as string[] | undefined) ?? [],
          }),
      },
    );
  });
}

/** Completes the temporary-password challenge and returns the live session. */
export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(toAuthError(err)),
      },
    );
  });
}

/**
 * Registers a user. Cognito emails a 6-digit confirmation code; the account is
 * unusable until {@link confirmSignUp} is called with it.
 *
 * <p>{@code givenName}/{@code familyName} are optional in the pool's schema, so
 * they're only attached when supplied.</p>
 */
export function signUp(params: {
  email: string;
  password: string;
  givenName?: string;
  familyName?: string;
}): Promise<{ userConfirmed: boolean }> {
  const attributes: CognitoUserAttribute[] = [
    new CognitoUserAttribute({ Name: "email", Value: params.email.trim() }),
  ];
  if (params.givenName?.trim()) {
    attributes.push(
      new CognitoUserAttribute({ Name: "given_name", Value: params.givenName.trim() }),
    );
  }
  if (params.familyName?.trim()) {
    attributes.push(
      new CognitoUserAttribute({ Name: "family_name", Value: params.familyName.trim() }),
    );
  }
  return new Promise((resolve, reject) => {
    requirePool().signUp(
      params.email.trim(),
      params.password,
      attributes,
      [],
      (err, result) => {
        if (err) return reject(toAuthError(err));
        resolve({ userConfirmed: result?.userConfirmed ?? false });
      },
    );
  });
}

/** Confirms a sign-up with the emailed code. */
export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmRegistration(code.trim(), true, (err) => {
      if (err) return reject(toAuthError(err));
      resolve();
    });
  });
}

/** Re-sends the sign-up confirmation code. */
export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).resendConfirmationCode((err) => {
      if (err) return reject(toAuthError(err));
      resolve();
    });
  });
}

/**
 * Starts a password reset — Cognito emails a code.
 *
 * <p>Resolves even when the address is unknown <i>if</i> the pool has
 * {@code preventUserExistenceErrors} on (it does), so this can't be used to
 * probe for registered addresses.</p>
 */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).forgotPassword({
      onSuccess: () => resolve(),
      // Fired once the code is on its way; the SDK calls this INSTEAD of
      // onSuccess for the code-delivery step, so it resolves the promise too.
      inputVerificationCode: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
    });
  });
}

/** Finishes a password reset with the emailed code. */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
    });
  });
}

/**
 * The session cached in local storage by the SDK, refreshed via the refresh
 * token when the access token has expired. Resolves null when there is no
 * usable session — signed out, or the refresh token is gone/revoked.
 *
 * <p>This is the whole of our token-renewal story: {@code getSession} performs
 * the {@code REFRESH_TOKEN_AUTH} exchange itself when needed, so a timer of our
 * own would only duplicate it.</p>
 */
export function currentSession(): Promise<CognitoUserSession | null> {
  const pool = userPool();
  const user = pool?.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session);
    });
  });
}

/** Clears the locally cached session. */
export function signOutLocal(): void {
  userPool()?.getCurrentUser()?.signOut();
}
